import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Input,
  Link as FluentLink,
  MessageBar,
  MessageBarBody,
  Skeleton,
  SkeletonItem,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ArrowLeft16Regular, Checkmark16Filled, Send20Filled } from "@fluentui/react-icons";
import { cellsToSlots, describeWeek, slotsToCells, type Cells, type Window } from "@gymplan/shared";
import { Logo } from "../components/Logo.tsx";
import { AvailabilityGrid } from "../components/AvailabilityGrid.tsx";
import { ChatLog } from "../components/ChatLog.tsx";
import { api } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { emit } from "../lib/analytics.ts";

type StepKey = "gym" | "session_length" | "days" | "hours" | "planning_day";
export interface SetupView {
  onboarded?: boolean;
  accepted?: boolean;
  current: StepKey | null;
  stepNumber: number;
  stepCount: number;
  question: string | null;
  placeholder: string | null;
  chips: string[];
  popularHint?: string | null;
  transcript: { role: "bot" | "me"; text: string }[];
  answers: { step: StepKey; label: string; value: string | null }[];
  proposedSlots: Window[];
  interpretation: string | null;
  defaultsUsed: string[];
  readyToConfirm: boolean;
}

const useStyles = makeStyles({
  page: { minHeight: "100vh", backgroundColor: tokens.colorNeutralBackground2, display: "flex", flexDirection: "column" },
  top: { display: "flex", alignItems: "center", gap: "16px", padding: "12px 24px", backgroundColor: tokens.colorNeutralBackground1, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, flexWrap: "wrap" },
  stepper: { display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto", flexWrap: "wrap" },
  stepDot: { display: "inline-grid", placeItems: "center", width: "22px", height: "22px", borderRadius: "50%", fontSize: tokens.fontSizeBase200, border: `1px solid ${tokens.colorNeutralStroke1}`, color: tokens.colorNeutralForeground3 },
  stepDotDone: { backgroundColor: tokens.colorBrandBackground, border: `1px solid ${tokens.colorBrandBackground}`, color: tokens.colorNeutralForegroundOnBrand },
  stepDotCurrent: { border: `1px solid ${tokens.colorBrandStroke1}`, color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  body: { flex: 1, width: "100%", maxWidth: "1040px", margin: "0 auto", padding: "24px", boxSizing: "border-box", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 320px", gap: "20px", alignItems: "start", "@media (max-width: 820px)": { gridTemplateColumns: "minmax(0, 1fr)", padding: "12px" } },
  chat: { display: "flex", flexDirection: "column", minHeight: "60vh", padding: 0, overflow: "hidden" },
  composer: { display: "flex", flexDirection: "column", gap: "10px", padding: "12px 16px 16px", borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  chips: { display: "flex", flexWrap: "wrap", gap: "8px" },
  inputRow: { display: "flex", gap: "8px" },
  summary: { display: "flex", flexDirection: "column", gap: "4px", padding: "16px", "@media (max-width: 820px)": { order: -1 } },
  row: { display: "flex", alignItems: "baseline", gap: "8px", padding: "8px 0", borderBottom: `1px solid ${tokens.colorNeutralStroke3}` },
  rowLabel: { width: "110px", flexShrink: 0, color: tokens.colorNeutralForeground3 },
  rowValue: { flex: 1 },
  confirm: { gridColumn: "1 / -1", minWidth: 0, display: "flex", flexDirection: "column", gap: "12px" },
  words: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
});

/** Setup flow after sign-up: a short chat on rails, then the pre-filled grid as a confirmation. */
export function SetupPage() {
  const s = useStyles();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const q = useQuery({ queryKey: ["setup"], queryFn: () => api.get<SetupView>("/api/setup") });
  const [v, setV] = useState<SetupView | null>(null);
  const [text, setText] = useState("");
  const [pending, setPending] = useState<{ step: StepKey; said: string } | null>(null);
  const [phase, setPhase] = useState<"chat" | "confirm">("chat");
  const [cells, setCells] = useState<Cells | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  const [settled, setSettled] = useState(false);
  const [animateFrom, setAnimateFrom] = useState(0);

  const toConfirm = (x: SetupView) => {
    setCells(slotsToCells(x.proposedSlots));
    setPhase("confirm");
  };
  useEffect(() => {
    if (q.data && !v) {
      setV(q.data);
      // A fresh start types out the welcome; coming back mid-way shows the conversation as it was.
      setAnimateFrom(q.data.transcript.some((m) => m.role === "me") ? q.data.transcript.length : 0);
      if (q.data.readyToConfirm && !q.data.current) toConfirm(q.data);
    }
  }, [q.data, v]);
  // When the last answer is in and the reply has finished "typing", move on to the week.
  useEffect(() => {
    if (!settled || !v?.readyToConfirm || v.current || phase !== "chat" || done.current) return;
    const t = setTimeout(() => toConfirm(v), 900);
    return () => clearTimeout(t);
  }, [settled, v, phase]);
  useEffect(() => {
    if (settled && v?.current) inputRef.current?.focus();
  }, [settled, v?.current]);
  // Leaving mid-way is a signal worth knowing about (#10).
  useEffect(() => {
    const h = () => {
      if (!done.current && v?.current) emit({ event: "setup_abandoned", properties: { step: v.current } });
    };
    window.addEventListener("pagehide", h);
    return () => window.removeEventListener("pagehide", h);
  }, [v?.current]);

  const send = useMutation({
    mutationFn: (b: { step: StepKey; text: string; via: "chip" | "text" }) => api.post<SetupView>("/api/setup/answer", b),
    onMutate: (b) => setPending({ step: b.step, said: b.text }),
    onSuccess: (next) => {
      setV(next);
      setText("");
    },
    onSettled: () => setPending(null),
  });
  const editStep = useMutation({
    mutationFn: (step: StepKey) => api.post<SetupView>("/api/setup/edit", { step }),
    onSuccess: (next) => {
      setV(next);
      setPhase("chat");
    },
  });
  const complete = useMutation({
    mutationFn: () => api.post("/api/setup/complete", { slots: cellsToSlots(cells!) }),
    onSuccess: async () => {
      done.current = true;
      await refresh();
      void qc.invalidateQueries({ queryKey: ["availability"] });
      nav("/", { replace: true, state: { justSetUp: true } });
    },
  });

  const submit = (t: string, via: "chip" | "text") => {
    if (!v?.current || !t.trim() || send.isPending) return;
    send.mutate({ step: v.current, text: t.trim(), via });
  };

  const gridWords = useMemo(() => (cells ? describeWeek(cellsToSlots(cells)) : ""), [cells]);
  const editedGrid = cells && v ? gridWords !== describeWeek(v.proposedSlots) : false;

  if (q.isLoading || !v) {
    return <div className={s.page}><div style={{ display: "grid", placeItems: "center", flex: 1 }}><Spinner label="Getting set up…" /></div></div>;
  }

  const stage = phase === "chat" ? 2 : 3;
  const transcript = pending ? [...v.transcript, { role: "me" as const, text: pending.said }] : v.transcript;

  return (
    <div className={s.page}>
      <header className={s.top}>
        <Logo size={26} />
        <Stepper stage={stage} />
      </header>

      <main className={s.body}>
        {phase === "chat" ? (
          <>
            <Card className={s.chat}>
              <ChatLog messages={transcript} thinking={send.isPending || editStep.isPending} animateFrom={animateFrom} onSettled={setSettled} />
              {v.current && settled && (
                <div className={s.composer}>
                  {v.popularHint && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{v.popularHint}</Text>}
                  <div className={s.chips}>
                    {v.chips.map((c) => (
                      <Button key={c} shape="circular" size="small" disabled={send.isPending} onClick={() => submit(c, "chip")}>{c}</Button>
                    ))}
                    {v.chips.length > 0 && v.current !== "gym" && <Button shape="circular" size="small" appearance="subtle" disabled={send.isPending} onClick={() => inputRef.current?.focus()}>Something else…</Button>}
                  </div>
                  <form className={s.inputRow} onSubmit={(e) => { e.preventDefault(); submit(text, "text"); }}>
                    <Input ref={inputRef} style={{ flex: 1 }} value={text} placeholder={v.placeholder ?? "Type your answer"} onChange={(_, d) => setText(d.value)} disabled={send.isPending} aria-label="Your answer" autoFocus />
                    <Button type="submit" appearance="primary" icon={<Send20Filled />} disabled={!text.trim() || send.isPending} aria-label="Send" />
                  </form>
                  {send.isError && <MessageBar intent="error"><MessageBarBody>{(send.error as Error).message}</MessageBarBody></MessageBar>}
                </div>
              )}
              {!v.current && v.readyToConfirm && settled && (
                <div className={s.composer}><Button appearance="primary" onClick={() => toConfirm(v)}>See your week</Button></div>
              )}
            </Card>
            <SummaryCard v={v} pendingStep={pending?.step ?? null} onEdit={(st) => editStep.mutate(st)} />
          </>
        ) : (
          <div className={s.confirm}>
            <div>
              <Button appearance="subtle" icon={<ArrowLeft16Regular />} onClick={() => setPhase("chat")}>Back to the questions</Button>
            </div>
            <Text as="h1" size={700} weight="semibold">Here's your week — look right?</Text>
            <Text className={s.words}>{gridWords}</Text>
            {v.defaultsUsed.length > 0 && !editedGrid && (
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>I've taken {v.defaultsUsed.join(" and ")}. Tap the grid to adjust.</Text>
            )}
            {cells && (
              <AvailabilityGrid
                value={cells}
                onChange={setCells}
                noQuickStart
                actions={
                  <Button appearance="primary" size="large" icon={<Checkmark16Filled />} disabled={complete.isPending || cellsToSlots(cells).length === 0} onClick={() => complete.mutate()}>
                    {complete.isPending ? "Saving…" : "Looks right — finish"}
                  </Button>
                }
              />
            )}
            {complete.isError && <MessageBar intent="error"><MessageBarBody>{(complete.error as Error).message}</MessageBarBody></MessageBar>}
            <Text style={{ color: tokens.colorNeutralForeground3 }}>This is just a starting point — tell me any time if your hours change.</Text>
          </div>
        )}
      </main>
    </div>
  );
}

function SummaryCard({ v, pendingStep, onEdit }: { v: SetupView; pendingStep: StepKey | null; onEdit: (s: StepKey) => void }) {
  const s = useStyles();
  return (
    <Card className={s.summary} aria-label="Your answers so far">
      <Text weight="semibold">Your week so far</Text>
      {v.answers.map((a) => (
        <div key={a.step} className={s.row}>
          <Text size={200} className={s.rowLabel}>{a.label}</Text>
          <span className={s.rowValue}>
            {pendingStep === a.step ? (
              <Skeleton><SkeletonItem size={12} /></Skeleton>
            ) : a.value ? (
              <Text size={300}>{a.value}</Text>
            ) : (
              <Text size={300} style={{ color: tokens.colorNeutralForeground4 }}>{v.current === a.step ? "Answering…" : "—"}</Text>
            )}
          </span>
          {a.value && v.current !== a.step && (
            <FluentLink as="button" onClick={() => onEdit(a.step)} style={{ fontSize: tokens.fontSizeBase200 }}>Change</FluentLink>
          )}
        </div>
      ))}
    </Card>
  );
}

function Stepper({ stage }: { stage: number }) {
  const s = useStyles();
  const steps = ["Your account", "How you work", "Check your week"];
  return (
    <nav className={s.stepper} aria-label={`Step ${stage} of ${steps.length}`}>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Step {stage} of {steps.length}</Text>
      {steps.map((label, i) => {
        const n = i + 1;
        return (
          <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span className={mergeClasses(s.stepDot, n < stage && s.stepDotDone, n === stage && s.stepDotCurrent)}>{n < stage ? "✓" : n}</span>
            <Text size={200} weight={n === stage ? "semibold" : "regular"}>{label}</Text>
          </span>
        );
      })}
    </nav>
  );
}
